import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  onIdTokenChanged,
} from 'firebase/auth';
import { auth } from './firebase';

/** Explicit allowlist entry (in addition to @artificiallly.com). */
export const ALLOWED_EMAIL = 'sam@beginthework.com';
export const ALLOWED_DOMAIN = 'artificiallly.com';

const AuthContext = createContext(null);

export function isAllowedEmail(email) {
  if (!email) return false;
  const normalized = email.toLowerCase();
  if (normalized === ALLOWED_EMAIL) return true;
  return normalized.endsWith(`@${ALLOWED_DOMAIN}`);
}

function sessionCookieSuffix() {
  if (typeof document === 'undefined') return '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  return `; Path=/; SameSite=Strict${secure}`;
}

function clearSessionCookie() {
  if (typeof document === 'undefined') return;
  document.cookie = `aa_id=; Max-Age=0${sessionCookieSuffix()}`;
}

async function setSessionCookie(user) {
  if (typeof document === 'undefined' || !user) return;
  const token = await user.getIdToken();
  document.cookie = `aa_id=${encodeURIComponent(token)}; Max-Age=3600${sessionCookieSuffix()}`;
}

async function rejectUnauthorized(setError) {
  clearSessionCookie();
  await fbSignOut(auth);
  setError(`Access restricted to ${ALLOWED_EMAIL} or @${ALLOWED_DOMAIN} accounts.`);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser && isAllowedEmail(firebaseUser.email)) {
        try {
          await setSessionCookie(firebaseUser);
        } catch (e) {
          console.error('Session cookie failed:', e);
        }
        setUser(firebaseUser);
        setError(null);
      } else {
        if (firebaseUser) {
          await rejectUnauthorized(setError);
        } else {
          clearSessionCookie();
        }
        setUser(null);
      }
      setLoading(false);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      const result = await signInWithPopup(auth, provider);
      if (result?.user && !isAllowedEmail(result.user.email)) {
        await rejectUnauthorized(setError);
      }
    } catch (e) {
      console.error('Login error:', e);
      if (e?.code !== 'auth/popup-closed-by-user') {
        setError(e?.message || 'Sign-in failed. Please try again.');
      }
    }
  }, []);

  const signOutUser = useCallback(async () => {
    await fbSignOut(auth);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        isAllowed: !!user,
        signInWithGoogle,
        signOut: signOutUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
